"use client";

import { useCallback, useEffect, useRef } from "react";

/**
 * Accessible modal built on the native <dialog> element.
 *
 * Why <dialog>: free focus trap, free Escape-to-close, free inert background.
 * The aria-modal + role="dialog" are implicit; just label it with aria-labelledby.
 *
 * Animation: pure CSS via @starting-style + transition on `open` attribute.
 * No framer-motion dependency on the open path so quick-add stays fast.
 */
interface ModalProps {
  open: boolean;
  onClose: () => void;
  labelledBy: string;
  children: React.ReactNode;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const SIZE_CLASS: Record<NonNullable<ModalProps["size"]>, string> = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-3xl",
};

export function Modal({
  open,
  onClose,
  labelledBy,
  children,
  size = "md",
  className = "",
}: ModalProps) {
  const ref = useRef<HTMLDialogElement>(null);

  // Sync open prop ↔ dialog state
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // Forward native close (Escape, form method=dialog, etc.) to parent
  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Click on backdrop (the dialog itself when not inside the inner panel) closes
  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDialogElement>) => {
      if (e.target === ref.current) onClose();
    },
    [onClose]
  );

  return (
    <dialog
      ref={ref}
      onClose={handleClose}
      onClick={handleBackdropClick}
      aria-labelledby={labelledBy}
      className={`m-auto w-[calc(100vw-2rem)] ${SIZE_CLASS[size]} rounded-sm border border-[var(--color-bone)]/20 bg-[var(--color-ink)] p-0 text-[var(--color-bone)] shadow-2xl backdrop:bg-black/70 backdrop:backdrop-blur-sm ${className}`}
    >
      {children}
    </dialog>
  );
}
